import { useEffect, useMemo, useRef } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import accessibilityInit from 'highcharts/modules/accessibility';
import { Info } from 'lucide-react';

import { Tooltip } from '@/components/ui/tooltip';
import type { Node } from '@/data-explorer-v2/entities/node';

import { GetChartData } from '../api/queries/get-chart-data';
import { ChartType } from '../entities/chart-type';
import type { Filters } from '../entities/filters';
import {
  detectDataAnomalies,
  summarizeAnomalies,
} from '../utils/detect-data-anomalies';

import {
  isValidSeriesData,
  optionsFromRequests,
} from './chart/options-from-requests';
import { chartTypeLabel } from './chart/utils';
import { EmptyChartState } from './empty-chart-state';

accessibilityInit(Highcharts);

export type ChartProps = {
  requests: { node: Node; query: UseQueryResult<GetChartData.Output> }[];
  chartType: ChartType;
  from: Date;
  to: Date;
  filterNoise: boolean;
  onUpdateFilters: (updated: Partial<Filters>) => void;
  /** Show anomaly detection hints for aggregated/unprocessed data levels */
  showAnomalyHints?: boolean;
};

export function Chart({
  requests,
  chartType,
  from,
  to,
  filterNoise,
  onUpdateFilters,
  showAnomalyHints = false,
}: ChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const isMountedRef = useRef(true);

  const options = useMemo(
    () => optionsFromRequests(requests, chartType, from, to),
    [requests, chartType, from, to],
  );

  // Detect data anomalies for aggregated/unprocessed data levels
  const anomalySummary = useMemo(() => {
    if (!showAnomalyHints) return null;

    // Collect all data points from completed queries for this chart type
    const allDataPoints: [number, number][] = [];
    for (const { query } of requests) {
      if (query.isLoading || query.isError || !query.data) continue;
      const chartData = query.data[chartType];
      if (!chartData) continue;
      for (const { data } of chartData) {
        if (isValidSeriesData(data)) {
          allDataPoints.push(...data);
        }
      }
    }

    if (allDataPoints.length === 0) return null;

    const anomalies = detectDataAnomalies(allDataPoints);
    return summarizeAnomalies(anomalies);
  }, [showAnomalyHints, requests, chartType]);

  useEffect(() => {
    isMountedRef.current = true;
    const chartInstance = chartRef.current;

    return () => {
      isMountedRef.current = false;
      if (chartInstance?.chart) {
        try {
          chartInstance.chart.destroy();
        } catch (error) {
          console.warn('Error destroying chart:', error);
        }
      }
    };
  }, []);

  if (!options) {
    return (
      <div className="chartContainer">
        <EmptyChartState
          chartType={chartType}
          from={from}
          to={to}
          filterNoise={filterNoise}
          onUpdateFilters={onUpdateFilters}
          compact
        />
      </div>
    );
  }

  // Additional safety check for series data
  const hasSafeSeriesData = options.series?.every((series) => {
    if ('data' in series && series.data) {
      return isValidSeriesData(series.data);
    }
    return true; // Allow series without data (loading states)
  });

  if (!hasSafeSeriesData) {
    console.error(
      `Unsafe series data detected for ${chartType}. Skipping chart render to prevent Highcharts error.`,
    );
    return (
      <div className="chartContainer">
        <div className="flex h-64 items-center justify-center text-red-500">
          ⚠️ Data format error for {chartTypeLabel[chartType]}
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="chartContainer relative">
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          constructorType="stockChart"
          options={options}
        />
        {showAnomalyHints && anomalySummary ? (
          <div className="mt-1 flex justify-end pr-4">
            <Tooltip
              delay
              position="left"
              content={
                <span className="block max-w-xs">
                  {anomalySummary.lines.map(
                    (line: string, i: number, arr: string[]) => (
                      <span
                        key={i}
                        className={i < arr.length - 1 ? 'mb-1 block' : ''}
                      >
                        {line}
                      </span>
                    ),
                  )}
                </span>
              }
            >
              <span className="inline-flex cursor-help items-center gap-1 rounded bg-gray-50 px-2 py-0.5 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700">
                <Info className="size-3" />
                Data pattern detected
              </span>
            </Tooltip>
          </div>
        ) : null}
      </div>
    );
  } catch (error) {
    console.error(`Error rendering chart for ${chartType}:`, error);
    return (
      <div className="chartContainer">
        <div className="flex h-64 items-center justify-center text-red-500">
          ⚠️ Chart rendering error for {chartTypeLabel[chartType]}
        </div>
      </div>
    );
  }
}
